import { MutableRefObject, useMemo } from 'react'

import { Box, HStack } from '@chakra-ui/react'
import { EDITOR_TOOLS } from '@editor/config/constants'
import { InspectionArea } from '@editor/interfaces/inspection'
import CameraToolbar from '@editor/pages/projects/common/CameraToolbar'
import { ArcballControls } from '@editor/services/threejs'
import { RootState, useAppDispatch } from '@editor/store/app'
import { useSelector } from 'react-redux'
import { PerspectiveCamera } from 'three'

import { Mode, Steps, Tools, ViewSides } from '../definitions'
import { setCurrentMode } from '../store'

const CameraToolbarManager = ({
  isAllowedToModify,

  leftInspectionArea,
  rightInspectionArea,

  mainCamera,
  cameraLeft,
  cameraRight,
  fineTuneCamera,

  arcballControlsLeftRef,
  arcballControlsRightRef,
  arcballControlsFineTuneRef,
  arcballControlsMainRef,
}: {
  isAllowedToModify: boolean

  leftInspectionArea: InspectionArea | null
  rightInspectionArea: InspectionArea | undefined

  mainCamera: PerspectiveCamera
  cameraLeft: PerspectiveCamera
  cameraRight: PerspectiveCamera
  fineTuneCamera: PerspectiveCamera

  arcballControlsLeftRef: MutableRefObject<ArcballControls | null>
  arcballControlsRightRef: MutableRefObject<ArcballControls | null>
  arcballControlsFineTuneRef: MutableRefObject<ArcballControls | null>
  arcballControlsMainRef: MutableRefObject<ArcballControls | null>
}) => {
  // Store
  const dispatch = useAppDispatch()
  const canvasRect = useSelector((state: RootState) => state.pcdAlignment.canvasRect)
  const currentStep = useSelector((state: RootState) => state.pcdAlignment.currentStep)
  const currentTool = useSelector((state: RootState) => state.pcdAlignment.currentTool)
  const currentSide = useSelector((state: RootState) => state.pcdAlignment.currentSide)
  const currentMode = useSelector((state: RootState) => state.pcdAlignment.currentMode)
  const isSplit = useMemo(
    () => currentStep !== Steps.FineTuneOrSave && currentTool === Tools.PCDAlignment,
    [currentStep, currentTool],
  )

  /**
   * Mimic Editor's selectedTool for the camera toolbar active state.
   */
  const selectedTool = useMemo(() => {
    if (currentMode === Mode.Focus) return EDITOR_TOOLS.FOCUS
    return ''
  }, [currentMode])

  /**
   * Get the active controls based on the current tool and step.
   */
  const activeControlsRef = useMemo(() => {
    if (currentTool === Tools.Overview) {
      return arcballControlsMainRef
    }
    if (currentStep === Steps.FineTuneOrSave) {
      return arcballControlsFineTuneRef
    }
    if (currentSide === ViewSides.Left) {
      return arcballControlsLeftRef
    }

    return arcballControlsRightRef
  }, [
    arcballControlsLeftRef,
    arcballControlsRightRef,
    arcballControlsFineTuneRef,
    arcballControlsMainRef,
    currentTool,
    currentStep,
    currentSide,
  ])

  /**
   * Get the active camera based on the current tool and step.
   */
  const activeCamera = useMemo(() => {
    if (currentTool === Tools.Overview) {
      return mainCamera
    }
    if (currentStep === Steps.FineTuneOrSave) {
      return fineTuneCamera
    }
    if (currentSide === ViewSides.Left) {
      return cameraLeft
    }

    return cameraRight
  }, [cameraLeft, cameraRight, fineTuneCamera, mainCamera, currentTool, currentStep, currentSide])

  if (!activeControlsRef.current) return null

  // TODO: need to deal with split screen too

  return (
    <HStack
      zIndex={90}
      id="camera-toolbar-manager"
      w="100%"
      h="100%"
      position="absolute"
      alignItems="end"
      pointerEvents="none"
    >
      {isSplit && leftInspectionArea ? (
        <CameraToolbar
          disableEditCameraProfile
          inspectionArea={leftInspectionArea}
          isAllowedToModify={isAllowedToModify}
          camera={activeCamera}
          controls={activeControlsRef.current}
          selectedTool={selectedTool}
          onChangeTool={(tool: string) => {
            if (tool === EDITOR_TOOLS.FOCUS) dispatch(setCurrentMode(Mode.Focus))
          }}
        />
      ) : null}
      <Box position="absolute" left={isSplit ? canvasRect.width / 2 : 0}>
        <CameraToolbar
          disableEditCameraProfile
          inspectionArea={rightInspectionArea}
          isAllowedToModify={isAllowedToModify}
          camera={activeCamera}
          controls={activeControlsRef.current}
          selectedTool={selectedTool}
          onChangeTool={(tool: string) => {
            if (tool === EDITOR_TOOLS.FOCUS) dispatch(setCurrentMode(Mode.Focus))
          }}
        />
      </Box>
    </HStack>
  )
}

export default CameraToolbarManager
